'use client';

import { useEffect, useMemo, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { Dropdown } from '../../atoms/Dropdown';
import { EmptyState } from '../../molecules/EmptyState';
import { emptyWriteReview } from '../../molecules/EmptyState/EmptyState';
import { Pagination } from '../../molecules/Pagination';
import { RichText } from '../../molecules/RichText';
import {
  UserProductReview,
  UserProductReviewPropsData,
} from '../UserProductReview';
import { WriteReviewModal } from '../WriteReviewModal';
import {
  ProductReviewFilterValues,
  ProductReviewSortByValues,
  ProductReviewsProps,
} from './ProductReviews.types';

export const ProductReviews = ({
  activeFilterValue,
  microcopy,
  onHelpfulVotesReview,
  onEditReview,
  onHelpfulVotesReply,
  onEditReply,
  onDeleteReply,
  onSubmitReply,
  onFilter,
  onSort,
  onPageChange,
  onSubmitReport,
  onClickEmptyStateCta,
  onEmailPublisher,
  onSubmitReview,
  onDeleteReview,
  reviews,
  start,
  end,
  total,
  currentPage,
  totalPages,
  reviewsOnLoad,
  writeReviewModal,
  className,
}: ProductReviewsProps) => {
  const MIN_REVIEWS_FOR_PAGINATION = 10;

  if (reviews?.length && microcopy?.filterOptions?.length !== 6) {
    throw new Error(
      "filterOptions microcopies must have exactly 6 items (1 default 'Rating' option plus 5 star ratings)"
    );
  }
  if (reviews?.length && microcopy?.sortOptions?.length !== 2) {
    throw new Error('sortOptions microcopies must have exactly 2 items');
  }

  const filterOptions = useMemo(
    () => [
      {
        label: microcopy.filterOptions[0],
        value: ProductReviewFilterValues.DEFAULT,
      },
      {
        label: microcopy.filterOptions[1],
        value: ProductReviewFilterValues.ONE_STAR,
      },
      {
        label: microcopy.filterOptions[2],
        value: ProductReviewFilterValues.TWO_STARS,
      },
      {
        label: microcopy.filterOptions[3],
        value: ProductReviewFilterValues.THREE_STARS,
      },
      {
        label: microcopy.filterOptions[4],
        value: ProductReviewFilterValues.FOUR_STARS,
      },
      {
        label: microcopy.filterOptions[5],
        value: ProductReviewFilterValues.FIVE_STARS,
      },
    ],
    [microcopy.filterOptions]
  );

  const sortByOptions = [
    {
      label: microcopy.sortOptions[0],
      value: ProductReviewSortByValues.MOST_RECENT,
    },
    {
      label: microcopy.sortOptions[1],
      value: ProductReviewSortByValues.TOP_RATED,
    },
  ];

  const [activeSortBy, setActiveSortBy] = useState({
    value: sortByOptions[0].value,
    label: sortByOptions[0].label,
  });

  const [activeRating, setActiveRating] = useState({
    value: filterOptions[0].value,
    label: filterOptions[0].label,
  });

  useEffect(() => {
    if (
      typeof activeFilterValue !== 'undefined' &&
      activeFilterValue !== activeRating.value
    ) {
      const found = filterOptions.find(
        (opt) => opt.value === activeFilterValue
      );
      if (found) {
        setActiveRating({ value: found.value, label: found.label });
      }
    }
  }, [activeFilterValue, activeRating.value, filterOptions]);

  const renderEmptyState = (
    <EmptyState
      microcopy={{
        title: microcopy?.emptyState?.title || '',
        description: microcopy?.emptyState?.description || '',
        cta: microcopy?.emptyState?.cta || '',
      }}
      onClick={onClickEmptyStateCta || (() => {})}
      modalTrigger={
        <WriteReviewModal
          title={writeReviewModal?.title}
          imageUrl={writeReviewModal?.imageUrl}
          version={writeReviewModal?.version}
          ownedDate={writeReviewModal?.ownedDate}
          microcopy={microcopy.emptyState?.writeReviewModal || emptyWriteReview}
          isMultiPackage={writeReviewModal?.isMultiPackage ?? false}
          onEmailPublisher={onEmailPublisher}
          onSubmitReview={onSubmitReview}
          reviewModalTrigger={
            <ButtonV2
              shape="square"
              size="small"
              label={microcopy.emptyState?.cta || ''}
            />
          }
        />
      }
    />
  );

  return (
    <section className={c(className)}>
      {!(reviewsOnLoad?.length ?? reviews?.length) ? (
        renderEmptyState
      ) : (
        <>
          <h2 className="mb-6 text-md font-medium">{microcopy.reviews}</h2>
          <nav className="mb-4 block w-full justify-between md:flex">
            <div className="flex gap-4">
              <div className="w-1/2 text-sm text-grey-600">
                <Dropdown
                  id="rating-dropdown"
                  options={filterOptions}
                  onSelect={(option) => {
                    setActiveRating({
                      value: option.value as (typeof filterOptions)[0]['value'],
                      label: option.label,
                    });
                    onFilter(
                      option.value as (typeof filterOptions)[0]['value']
                    );
                  }}
                  isDarkTheme={false}
                  noSelectedOptionPlaceholder={microcopy.rating}
                  selectedOption={
                    activeRating && {
                      value: activeRating.value,
                      label: activeRating.label,
                    }
                  }
                  label={microcopy.rating}
                  className="max-w-10 text-sm [&_label]:font-normal [&_label]:text-grey-600"
                />
              </div>
              <div className="w-1/2 text-sm text-grey-600">
                <Dropdown
                  id="sort-by-dropdown"
                  options={sortByOptions}
                  onSelect={(option) => {
                    setActiveSortBy({
                      value: option.value as ProductReviewSortByValues,
                      label: option.label,
                    });
                    onSort(option.value as ProductReviewSortByValues);
                  }}
                  isDarkTheme={false}
                  noSelectedOptionPlaceholder={microcopy.sortBy}
                  selectedOption={
                    activeSortBy && {
                      value: activeSortBy.value,
                      label: activeSortBy.label,
                    }
                  }
                  label={microcopy.sortBy}
                  className="[&_label]:font-normal [&_label]:text-grey-600"
                />
              </div>
            </div>
            <RichText
              className="mt-4 flex items-center text-sm text-grey-600 md:mt-0"
              html={interpolateStringWithData(microcopy.results, {
                start,
                end,
                total,
              })}
              type="p"
            />
          </nav>
          <div>
            {reviews?.length ? (
              reviews.map((review: UserProductReviewPropsData) => (
                <UserProductReview
                  onDeleteReview={onDeleteReview}
                  onEmailPublisher={onEmailPublisher}
                  onSubmitReview={onSubmitReview}
                  className="mb-4"
                  key={review.reviewDetails.id}
                  onSubmitReport={onSubmitReport}
                  onEditReview={() => {
                    const editData = {
                      id: review.reviewDetails.id,
                      rating: review.reviewDetails.stars.value,
                      title: review.reviewDetails.title || '',
                      review: review.reviewDetails.review || '',
                    };
                    return onEditReview(review.reviewDetails.id, editData);
                  }}
                  onEditReply={() => {
                    if (review.reviewDetails.reply) {
                      onEditReply(review.reviewDetails.reply.id);
                    }
                  }}
                  onDeleteReply={() => {
                    if (review.reviewDetails.reply) {
                      onDeleteReply(review.reviewDetails.reply.id);
                    }
                  }}
                  onSubmitReply={onSubmitReply}
                  microcopy={{
                    reviewerInfo: microcopy.reviewerInfo,
                    reviewDetails: microcopy.reviewDetails,
                  }}
                  onHelpfulVotesReply={() => {
                    if (review.reviewDetails.reply) {
                      onHelpfulVotesReply(review.reviewDetails.reply.id);
                    }
                  }}
                  onHelpfulVotesReview={() =>
                    onHelpfulVotesReview(review.reviewDetails.id)
                  }
                  {...review}
                />
              ))
            ) : (
              <div className="text-sm text-grey-600">{renderEmptyState}</div>
            )}
          </div>
          {(reviews?.length ?? 0) >= MIN_REVIEWS_FOR_PAGINATION && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              microcopy={microcopy.pagination}
              onPageChange={(page) => onPageChange(page)}
              className="mt-6 flex justify-center"
              maxVisiblePages={4}
            />
          )}
        </>
      )}
    </section>
  );
};
